import { useSelector } from 'react-redux';
import { selectClaimHistory } from '../../features/claims/claimSlice';
import { formatDateTime } from '../../utils/dateUtils';
import OmCard from '../ui/OmCard';
import OmBadge from '../ui/OmBadge';

const StatusHistory = ({ claim }) => {
  const allHistory = useSelector(selectClaimHistory);
  
  // Filter to show only status-relevant actions
  const statusRelevantActions = [
    'CLAIM_CREATED',
    'CLAIM_STATUS_CHANGED', 
    'RECOMMEND_FOR_APPROVAL',
    'APPROVE_CLAIM',
    'REJECT_CLAIM',
    'PAYMENT_AUTHORIZED',
    'PAYMENT_INITIATED',
    'PAYMENT_PROCESSED'
  ];
  
  const history = allHistory.filter(entry => 
    statusRelevantActions.includes(entry.action)
  );
  
  // Debug logging for status history
  console.log('🔍 StatusHistory Debug:', {
    totalHistory: allHistory.length,
    filteredHistory: history.length,
    historyEntries: history.map(h => ({
      id: h.id,
      action: h.action,
      hasMetadata: !!h.metadata,
      newStatus: h.metadata?.newStatus,
      details: h.details
    }))
  });
  
  const getStatusBadgeProps = (entry) => {
    const { action, metadata } = entry;
    
    // Map actions to badge properties based on the resulting status
    switch (action) {
      case 'CLAIM_CREATED':
        return { variant: 'dot', color: 'primary' };
      case 'CLAIM_STATUS_CHANGED':
        if (metadata?.newStatus) {
          switch (metadata.newStatus) {
            case 'UNDER_REVIEW': 
            case 'MISSING_DOCUMENTS': 
              return { variant: 'dot', color: 'warning' };
            case 'APPROVED': 
            case 'PAID':
              return { variant: 'dot', color: 'success' };
            case 'REJECTED': 
              return { variant: 'dot', color: 'danger' };
            case 'PAYMENT_PENDING': 
              return { variant: 'dot', color: 'info' };
            default: 
              return { variant: 'dot', color: 'neutral' };
          }
        }
        return { variant: 'dot', color: 'neutral' };
      case 'RECOMMEND_FOR_APPROVAL':
        return { variant: 'dot', color: 'info' };
      case 'APPROVE_CLAIM':
        return { variant: 'dot', color: 'success' };
      case 'REJECT_CLAIM':
        return { variant: 'dot', color: 'danger' };
      case 'PAYMENT_AUTHORIZED':
        return { variant: 'dot', color: 'info' };
      case 'PAYMENT_INITIATED':
        return { variant: 'dot', color: 'warning' };
      case 'PAYMENT_PROCESSED':
        return { variant: 'dot', color: 'success' };
      default:
        return { variant: 'dot', color: 'neutral' };
    }
  };

  const getActionLabel = (entry) => {
    const { action, metadata } = entry;
    
    console.log('🏷️ getActionLabel Debug:', {
      entryId: entry.id,
      action,
      hasMetadata: !!metadata,
      metadata: metadata,
      newStatus: metadata?.newStatus
    });
    
    // Convert action codes to clean status labels
    switch (action) {
      case 'CLAIM_CREATED':
        return 'Submitted';
      case 'CLAIM_STATUS_CHANGED':
        // Extract new status from metadata if available
        if (metadata?.newStatus) {
          switch (metadata.newStatus) {
            case 'UNDER_REVIEW': return 'Under Review';
            case 'MISSING_DOCUMENTS': return 'Missing Documents';
            case 'APPROVED': return 'Approved';
            case 'REJECTED': return 'Rejected';
            case 'PAYMENT_PENDING': return 'Payment Pending';
            case 'PAID': return 'Paid';
            default: return metadata.newStatus.replace('_', ' ');
          }
        }
        return 'Status Changed';
      case 'RECOMMEND_FOR_APPROVAL':
        return 'Recommended for Approval';
      case 'APPROVE_CLAIM':
        return 'Approved';
      case 'REJECT_CLAIM':
        return 'Rejected';
      case 'PAYMENT_AUTHORIZED':
        return 'Payment Authorized';
      case 'PAYMENT_INITIATED':
        return 'Payment Initiated';
      case 'PAYMENT_PROCESSED':
        return 'Paid';
      default:
        return action.replace(/_/g, ' ');
    }
  };

  return (
    <OmCard className="bg-crystal-white shadow-soft border border-steel/20 rounded-lg overflow-hidden transition-all duration-300 ease-in-out hover:shadow-lg hover:-translate-y-1 hover:border-fresh-leaf/30 focus-within:ring-2 focus-within:ring-fresh-leaf/25 focus-within:border-fresh-leaf/40">
      <div className="px-6 sm:px-8 pt-6 sm:pt-8 pb-4 sm:pb-6 border-b border-steel/15">
        <h3 className="text-xl sm:text-2xl font-semibold text-graphite mb-3">Status History</h3>
        <p className="text-sm sm:text-base text-steel">Timeline of claim status changes</p>
      </div>
      <div className="p-6 sm:p-8">
        <div className="space-y-6 sm:space-y-8">
          {history.length > 0 ? (
            history.map((entry) => {
              const badgeProps = getStatusBadgeProps(entry);
              const statusLabel = getActionLabel(entry);
              
              return (
                <div key={entry.id} className="flex items-start gap-4 sm:gap-5 group hover:bg-silver-mist/30 -mx-2 px-2 py-3 rounded-lg transition-all duration-300 ease-in-out hover:shadow-sm cursor-default">
                  <OmBadge
                    variant={badgeProps.variant}
                    color={badgeProps.color}
                    size="small"
                    className="mt-1 flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:shadow-sm"
                  >
                    {statusLabel}
                  </OmBadge>
                  <div className="flex-1 min-w-0 space-y-2">
                    <p className="text-sm sm:text-base font-semibold text-graphite transition-all duration-200 group-hover:text-deep-jungle">
                      {statusLabel}
                    </p>
                    <p className="text-xs sm:text-sm text-steel transition-all duration-200 group-hover:text-forest-fern">
                      {formatDateTime(entry.changedAt)}
                      {entry.changedBy && ` by ${entry.changedBy.username}`}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex items-start gap-4 sm:gap-5 group hover:bg-silver-mist/30 -mx-2 px-2 py-3 rounded-lg transition-all duration-300 ease-in-out hover:shadow-sm cursor-default">
              <OmBadge
                variant="dot"
                color="primary"
                size="small"
                className="mt-1 flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:shadow-sm"
              >
                Submitted
              </OmBadge>
              <div className="flex-1 min-w-0 space-y-2">
                <p className="text-sm sm:text-base font-semibold text-graphite transition-all duration-200 group-hover:text-deep-jungle">Submitted</p>
                <p className="text-xs sm:text-sm text-steel transition-all duration-200 group-hover:text-forest-fern">
                  {formatDateTime(claim.submittedAt)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </OmCard>
  );
};

export default StatusHistory;
